import React, {useState} from 'react';
import {TruvuContainer} from '../components/containers/TruvuContainer';
import {
  Stack,
  Card,
  CardContent,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {useHistory} from 'react-router';
import RelayRenderer from '../RelayRenderer';
import graphql from 'babel-plugin-relay/macro';
import {useMutation} from 'relay-hooks';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import GetAppIcon from '@mui/icons-material/GetApp';
import {ReportsPageQuery} from '../__generated__/ReportsPageQuery.graphql';

const query = graphql`
  query ReportsPageQuery {
    me {
      user {
        company {
          id
          name
        }
      }
    }
  }
`;

const generateTourByCompanyReportMutation = graphql`
  mutation ReportsPageGenerateTourByCompanyReportMutation(
    $input: GenerateTourByCompanyReportInput!
  ) {
    generateTourByCompanyReport(input: $input) {
      fileContent
      fileName
      mimeType
    }
  }
`;

const generateSummaryByUserReportMutation = graphql`
  mutation ReportsPageGenerateSummaryByUserReportMutation(
    $input: GenerateSummaryByUserReportInput!
  ) {
    generateSummaryByUserReport(input: $input) {
      fileContent
      fileName
      mimeType
    }
  }
`;

// Define all available reports in one place for easy management
const REPORT_TYPES = {
  TOURS_BY_COMPANY: 'tours-by-company',
  SUMMARY_BY_USER: 'summary-by-user',
};

export function ReportsPage() {
  const {push} = useHistory();
  const [loading, setLoading] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('csv');
  const [selectedReport, setSelectedReport] = useState(null);

  const [generateTourByCompanyReport] = useMutation(
    generateTourByCompanyReportMutation
  );

  const [generateSummaryByUserReport] = useMutation(
    generateSummaryByUserReportMutation
  );

  // Handle file download for any report
  const handleDownloadFile = (data) => {
    const fileData = data.fileContent;
    const fileName = data.fileName;
    const mimeType = data.mimeType;

    // Convert base64 to Blob
    const byteCharacters = atob(fileData);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, {type: mimeType});

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Generate report based on type
  const handleGenerateReport = (companyId) => {
    setLoading(true);

    const reportGenerators = {
      [REPORT_TYPES.TOURS_BY_COMPANY]: () => {
        generateTourByCompanyReport({
          variables: {
            input: {
              companyId,
              exportType,
            },
          },
          onCompleted: (data, errors) => {
            setLoading(false);
            setExportDialogOpen(false);

            if (errors) {
              console.error('Error generating report:', errors);
              return;
            }

            handleDownloadFile(data.generateTourByCompanyReport);
          },
          onError: handleReportError,
        });
      },
      [REPORT_TYPES.SUMMARY_BY_USER]: () => {
        generateSummaryByUserReport({
          variables: {
            input: {
              companyId,
              exportType,
            },
          },
          onCompleted: (data, errors) => {
            setLoading(false);
            setExportDialogOpen(false);

            if (errors) {
              console.error('Error generating report:', errors);
              return;
            }

            handleDownloadFile(data.generateSummaryByUserReport);
          },
          onError: handleReportError,
        });
      },
    };

    // Execute the appropriate report generator
    const reportGenerator = reportGenerators[selectedReport.id];
    if (reportGenerator) {
      reportGenerator();
    } else {
      console.error('Unknown report type:', selectedReport.id);
      setLoading(false);
      setExportDialogOpen(false);
    }
  };

  const handleReportError = (err) => {
    setLoading(false);
    setExportDialogOpen(false);
    console.error('Error generating report:', err);
  };

  const handleReportClick = (report) => {
    setSelectedReport(report);
    setExportDialogOpen(true);
  };

  // List of all available reports - easy to add new ones here
  const reports = [
    {
      id: REPORT_TYPES.TOURS_BY_COMPANY,
      name: 'Tours By Company',
      description:
        'A comprehensive report of all tours associated with your company',
      icon: <DescriptionIcon />,
    },
    {
      id: REPORT_TYPES.SUMMARY_BY_USER,
      name: 'User Summary',
      description:
        'A summary report of activity and tour metrics grouped by user',
      icon: <PeopleIcon />,
    },
    // Add new reports here as needed
  ];

  return (
    <RelayRenderer<ReportsPageQuery> query={query}>
      {(data) => {
        const companyId = data.me?.user?.company?.id;

        return (
          <TruvuContainer
            onGoBack={() => {
              push('/account');
            }}
          >
            <Stack spacing={0.5} sx={{mb: 2, mt: 2}}>
              <Typography variant="h1" color="text.primary">
                Reports
              </Typography>
              <Typography variant="h3" color="text.secondary">
                Generate and download reports for your business
              </Typography>
            </Stack>

            <Card sx={{mb: 4}}>
              <CardContent>
                <Typography variant="h5" component="div" gutterBottom>
                  Available Reports
                </Typography>
                <List>
                  {reports.map((report) => (
                    <ListItem
                      key={report.id}
                      button
                      onClick={() => handleReportClick(report)}
                    >
                      <ListItemIcon>{report.icon}</ListItemIcon>
                      <ListItemText
                        primary={report.name}
                        secondary={report.description}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>

            <Dialog
              open={exportDialogOpen}
              onClose={() => setExportDialogOpen(false)}
              aria-labelledby="export-dialog-title"
            >
              <DialogTitle id="export-dialog-title">
                Export {selectedReport?.name}
              </DialogTitle>
              <DialogContent>
                <FormControl fullWidth sx={{mt: 2}}>
                  <InputLabel id="export-type-label">Export Format</InputLabel>
                  <Select
                    labelId="export-type-label"
                    value={exportType}
                    label="Export Format"
                    onChange={(e) => setExportType(e.target.value)}
                  >
                    <MenuItem value="csv">CSV</MenuItem>
                    {/*<MenuItem value="xlsx">Excel</MenuItem>*/}
                    <MenuItem value="pdf">PDF</MenuItem>
                  </Select>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setExportDialogOpen(false)}
                  color="inherit"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleGenerateReport(companyId)}
                  color="primary"
                  variant="contained"
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <GetAppIcon />
                    )
                  }
                  disabled={loading || !companyId}
                >
                  {loading ? 'Generating...' : 'Download'}
                </Button>
              </DialogActions>
            </Dialog>
          </TruvuContainer>
        );
      }}
    </RelayRenderer>
  );
}
